import { moveSlidingToggle } from './sliding-toggle.js';
const PLAYER_TAG = 'landing-brew-guide-player';
const LOAD_TIMEOUT_MS = 12000;
const CAPTION_LOAD_TIMEOUT_MS = 8000;

// Recipe switch: pause immediately, switch media/transcript, then fade recipe details.
// CSS owns the interruptible 180ms fade, text blur and selector movement.
const RECIPES_TAG = 'landing-brew-recipes';

if (!customElements.get(RECIPES_TAG)) {
  class LandingBrewRecipes extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.tabs = [...this.querySelectorAll('[data-brew-recipe-tab]')];
      this.panels = [...this.querySelectorAll('[data-brew-recipe-panel]')];
      this.detailPanels = [...this.querySelectorAll('[data-brew-recipe-details], [data-brew-recipe-transcript]')];
      if (!this.tabs.length || this.tabs.length !== this.panels.length) return;
      this.initialized = true;
      this.handleClick = (event) => {
        const tab = event.target.closest('[data-brew-recipe-tab]');
        if (this.tabs.includes(tab)) this.selectRecipe(tab.dataset.brewRecipeTab);
      };
      this.handleKeydown = (event) => {
        const index = this.tabs.indexOf(event.target);
        if (index < 0) return;
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.tabs[next].focus({ preventScroll: true });
        this.selectRecipe(this.tabs[next].dataset.brewRecipeTab);
      };
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.selectRecipe(this.dataset.activeRecipe || this.tabs[0].dataset.brewRecipeTab);
      this.querySelector('[role="tablist"]').hidden = false;
      this.dataset.enhanced = 'true';
    }

    selectRecipe(key) {
      if (!this.tabs.some((tab) => tab.dataset.brewRecipeTab === key)) return;
      moveSlidingToggle(this.querySelector('.landing-brew-recipes__indicator'), this.tabs.findIndex((tab) => tab.dataset.brewRecipeTab === key));
      const current = this.panels.find((panel) => !panel.hidden);
      const currentPlayer = current?.querySelector(PLAYER_TAG);
      if (currentPlayer?.initialized) this.captionsVisible = currentPlayer.captionsVisible;

      this.tabs.forEach((tab) => {
        const active = tab.dataset.brewRecipeTab === key;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        const active = panel.dataset.brewRecipePanel === key;
        const player = panel.querySelector(PLAYER_TAG);
        if (!active) player?.suspendPlayback?.();
        panel.hidden = !active;
        panel.inert = !active;
        panel.setAttribute('aria-hidden', String(!active));
        if (active && player?.initialized) {
          player.setCaptionVisibility(this.captionsVisible ?? false);
          player.updateCue(false);
        }
      });
      this.detailPanels.forEach((panel) => {
        const active = (panel.dataset.brewRecipeDetails || panel.dataset.brewRecipeTranscript) === key;
        panel.hidden = !active;
        panel.inert = !active;
        panel.setAttribute('aria-hidden', String(!active));
      });
      this.dataset.activeRecipe = key;
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.querySelectorAll(PLAYER_TAG).forEach((player) => player.suspendPlayback?.());
      this.initialized = false;
    }
  }
  customElements.define(RECIPES_TAG, LandingBrewRecipes);
}

if (!customElements.get(PLAYER_TAG)) {
  class LandingBrewGuidePlayer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.startLink = this.querySelector('[data-brew-guide-start]');
      this.startLabel = this.querySelector('[data-brew-guide-start-label]');
      this.status = this.querySelector('[data-brew-guide-status]');
      this.directLink = this.querySelector('[data-brew-guide-direct]');
      this.mediaTemplate = this.querySelector('[data-brew-guide-media]');
      this.captionRegion = this.closest('.landing-brew-guide__figure')?.querySelector(
        '[data-brew-guide-inline-captions]'
      );
      this.cueElement = this.captionRegion?.querySelector('[data-brew-guide-cue]');
      this.captionReveal = this.captionRegion?.querySelector('[data-brew-guide-caption-reveal]');
      this.captionToggle = this.captionRegion?.querySelector('[data-brew-guide-caption-toggle]');
      this.captionToggleLabel = this.captionRegion?.querySelector('[data-brew-guide-caption-toggle-label]');
      this.handleStart = this.handleStart.bind(this);
      this.handleKeydown = (event) => {
        if (event.key === ' ') this.handleStart(event);
      };
      this.startLink?.setAttribute('role', 'button');
      this.handleVisibility = this.handleVisibility.bind(this);
      this.handleSectionUnload = this.handleSectionUnload.bind(this);
      this.handleCaptionToggle = this.handleCaptionToggle.bind(this);
      this.handleFullscreenChange = this.handleFullscreenChange.bind(this);
      this.captionsVisible = this.closest(RECIPES_TAG)?.captionsVisible ?? false;
      if (this.captionToggle) {
        this.captionToggle.hidden = false;
        this.captionToggle.setAttribute('aria-pressed', 'false');
      }
      this.setCaptionVisibility(this.captionsVisible);

      this.startLink?.addEventListener('click', this.handleStart);
      this.startLink?.addEventListener('keydown', this.handleKeydown);
      this.captionToggle?.addEventListener('click', this.handleCaptionToggle);
      document.addEventListener('visibilitychange', this.handleVisibility);
      document.addEventListener('shopify:section:unload', this.handleSectionUnload);
      document.addEventListener('fullscreenchange', this.handleFullscreenChange);
      this.dataset.state = 'ready';
    }

    disconnectedCallback() {
      this.teardown();
    }

    handleStart(event) {
      event.preventDefault();
      if (this.dataset.state === 'loading') return;
      this.startPlayback();
    }

    handleVisibility() {
      if (document.hidden && this.video && !this.video.paused) {
        this.video.pause();
      }
    }

    handleSectionUnload(event) {
      if (event.target === this || event.target?.contains(this)) {
        this.teardown();
      }
    }

    handleCaptionToggle() {
      this.setCaptionVisibility(!this.captionsVisible);
    }

    setCaptionVisibility(visible) {
      this.captionsVisible = visible;
      if (!this.captionRegion) return;
      this.captionRegion.dataset.expanded = String(this.captionsVisible);
      this.captionReveal?.setAttribute('aria-hidden', String(!this.captionsVisible));
      if (!['loading', 'error'].includes(this.captionRegion.dataset.state)) {
        this.captionRegion.dataset.state = this.captionsVisible ? 'ready' : 'off';
        this.captionToggleLabel.textContent = this.captionsVisible ? 'Hide captions' : 'Show captions';
        this.captionToggle.setAttribute('aria-pressed', String(this.captionsVisible));
      }
      if (this.textTrack) {
        this.syncTextTrackMode();
        this.updateCue(false);
      }
    }

    suspendPlayback() {
      this.video?.pause();
      this.cueAnimation?.cancel();
      if (this.cueElement) this.cueElement.textContent = '';
      if (this.dataset.state === 'loading') {
        this.removeVideo();
        this.dataset.state = 'ready';
        this.startLabel.textContent = 'Watch the brew';
        this.status.textContent = '';
        if (this.captionToggle) this.captionToggle.disabled = false;
        if (this.captionRegion) this.captionRegion.dataset.state = 'off';
        this.setCaptionVisibility(this.captionsVisible);
      }
    }

    isVideoFullscreen() {
      return document.fullscreenElement === this.video || Boolean(this.video?.webkitDisplayingFullscreen);
    }

    handleFullscreenChange() {
      if (!this.captionRegion || !this.video) return;
      const fullscreen = this.isVideoFullscreen();
      this.captionRegion.hidden = fullscreen;
      this.syncTextTrackMode();
    }

    syncTextTrackMode() {
      if (!this.textTrack) return;
      this.textTrack.mode = this.isVideoFullscreen() && this.captionsVisible ? 'showing' : 'hidden';
    }

    cueText(cue) {
      if (typeof cue.getCueAsHTML === 'function') return cue.getCueAsHTML().textContent || '';
      return cue.text || '';
    }

    updateCue(animate = true) {
      if (!this.cueElement || !this.textTrack) return;
      const text = [...(this.textTrack.activeCues || [])].map((cue) => this.cueText(cue)).join(' ');
      this.cueAnimation?.cancel();
      this.cueElement.textContent = text;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!text || !this.captionsVisible || !animate || reducedMotion || this.video?.seeking || this.video?.paused) return;
      this.cueAnimation = this.cueElement.animate(
        [
          { opacity: 0, transform: 'translateY(4px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 150, easing: 'ease-out' }
      );
    }

    setupCaptionTrack(trackElement, video) {
      if (!this.captionRegion || !trackElement.track || this.video !== video) return;
      window.clearTimeout(this.captionLoadTimer);
      this.captionLoadTimer = undefined;
      this.textTrack = trackElement.track;
      this.handleCueChange = () => this.updateCue(true);
      this.handleCaptionSeek = () => this.updateCue(false);
      this.handleWebkitFullscreen = () => this.handleFullscreenChange();
      this.textTrack.addEventListener('cuechange', this.handleCueChange);
      video.addEventListener('seeking', this.handleCaptionSeek);
      video.addEventListener('seeked', this.handleCaptionSeek);
      video.addEventListener('pause', this.handleCaptionSeek);
      video.addEventListener('webkitbeginfullscreen', this.handleWebkitFullscreen);
      video.addEventListener('webkitendfullscreen', this.handleWebkitFullscreen);
      this.captionRegion.dataset.state = this.captionsVisible ? 'ready' : 'off';
      this.captionToggle.disabled = false;
      this.captionToggleLabel.textContent = this.captionsVisible ? 'Hide captions' : 'Show captions';
      this.captionToggle.setAttribute('aria-pressed', String(this.captionsVisible));
      this.syncTextTrackMode();
      this.updateCue(false);
    }

    showCaptionError() {
      if (!this.captionRegion) return;
      window.clearTimeout(this.captionLoadTimer);
      this.captionLoadTimer = undefined;
      this.captionRegion.dataset.state = 'error';
      this.cueElement.textContent = '';
      this.captionToggle.disabled = true;
      this.captionToggle.removeAttribute('aria-pressed');
      this.captionToggleLabel.textContent = 'Captions unavailable';
    }

    getSources() {
      if (!this.mediaTemplate) return [];

      return [...this.mediaTemplate.content.querySelectorAll('[data-brew-guide-source]')]
        .map((source) => ({
          src: source.dataset.src,
          type: source.dataset.type?.toLowerCase() || '',
          width: Number.parseInt(source.dataset.width || '0', 10) || 0,
        }))
        .filter((source) => source.src);
    }

    selectSource(video) {
      const sources = this.getSources();
      const hlsSource = sources.find(
        (source) => source.type.includes('mpegurl') || source.src.toLowerCase().includes('.m3u8')
      );

      if (hlsSource && video.canPlayType('application/vnd.apple.mpegurl')) {
        return hlsSource;
      }

      const mp4Sources = sources
        .filter((source) => source.type.includes('mp4') || source.src.toLowerCase().includes('.mp4'))
        .sort((first, second) => first.width - second.width);

      if (mp4Sources.length === 0) return sources[0];

      const renderedWidth = Math.max(this.getBoundingClientRect().width, 1);
      const targetWidth = Math.min(Math.ceil(renderedWidth * (window.devicePixelRatio || 1)), 1920);
      const cappedSources = mp4Sources.filter((source) => source.width === 0 || source.width <= 1920);
      const candidates = cappedSources.length > 0 ? cappedSources : [mp4Sources[0]];

      return (
        candidates.find((source) => source.width === 0 || source.width >= targetWidth) ||
        candidates[candidates.length - 1]
      );
    }

    startPlayback() {
      this.removeVideo();
      const playbackAttempt = ++this.playbackAttempt;
      this.directLink.hidden = true;
      this.startLabel.textContent = 'Loading video';
      this.status.textContent = 'Loading the brew video…';
      this.dataset.state = 'loading';

      const video = document.createElement('video');
      const source = this.selectSource(video);

      if (!source) {
        this.showFailure('This browser cannot play the available video.');
        return;
      }

      video.className = 'landing-brew-guide__video';
      video.controls = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.crossOrigin = 'anonymous';
      if (this.dataset.videoLabel) video.setAttribute('aria-label', this.dataset.videoLabel);

      const trackData = this.mediaTemplate?.content.querySelector('[data-brew-guide-track]');
      if (trackData?.dataset.src) {
        const track = document.createElement('track');
        track.kind = 'captions';
        track.srclang = 'en';
        track.label = 'English';
        track.addEventListener('load', () => this.setupCaptionTrack(track, video), { once: true });
        track.addEventListener(
          'error',
          () => {
            if (this.video === video) this.showCaptionError();
          },
          { once: true }
        );
        video.append(track);
        track.track.mode = 'hidden';
        track.src = trackData.dataset.src;
        this.captionRegion.dataset.state = 'loading';
        this.captionToggle.disabled = true;
        this.captionToggle.removeAttribute('aria-pressed');
        this.captionToggleLabel.textContent = 'Captions loading';
        this.captionLoadTimer = window.setTimeout(() => {
          if (this.video === video && !this.textTrack) this.showCaptionError();
        }, CAPTION_LOAD_TIMEOUT_MS);
      }

      video.src = source.src;

      video.addEventListener(
        'playing',
        () => {
          if (this.playbackAttempt === playbackAttempt) this.handlePlaying();
        },
        { once: true }
      );
      video.addEventListener(
        'error',
        () => {
          if (this.playbackAttempt === playbackAttempt) {
            this.showFailure('Unable to load the video. Check your connection and try again.');
          }
        },
        { once: true }
      );
      this.video = video;
      this.append(video);
      this.loadTimer = window.setTimeout(() => {
        if (this.playbackAttempt === playbackAttempt) {
          this.showFailure('The video is taking longer than expected to load.');
        }
      }, LOAD_TIMEOUT_MS);

      const playAttempt = video.play();
      if (playAttempt) {
        playAttempt.catch(() => {
          if (this.playbackAttempt === playbackAttempt) {
            this.showFailure('Unable to start the video. Try again or open it directly.');
          }
        });
      }
    }

    handlePlaying() {
      window.clearTimeout(this.loadTimer);
      this.loadTimer = undefined;
      this.dataset.state = 'playing';
      this.status.textContent = '';
      this.video?.focus({ preventScroll: true });
    }

    showFailure(message) {
      this.removeVideo();
      if (this.captionRegion) {
        this.captionRegion.dataset.state = 'off';
        this.captionToggle.disabled = false;
        this.setCaptionVisibility(this.captionsVisible);
      }
      this.dataset.state = 'error';
      this.status.textContent = message;
      this.startLabel.textContent = 'Retry video';
      this.directLink.hidden = false;
      this.startLink?.focus({ preventScroll: true });
    }

    removeVideo() {
      window.clearTimeout(this.captionLoadTimer);
      this.captionLoadTimer = undefined;
      this.playbackAttempt = (this.playbackAttempt || 0) + 1;
      window.clearTimeout(this.loadTimer);
      this.loadTimer = undefined;

      this.cueAnimation?.cancel();
      this.cueAnimation = undefined;
      this.textTrack?.removeEventListener('cuechange', this.handleCueChange);
      this.video?.removeEventListener('seeking', this.handleCaptionSeek);
      this.video?.removeEventListener('seeked', this.handleCaptionSeek);
      this.video?.removeEventListener('pause', this.handleCaptionSeek);
      this.video?.removeEventListener('webkitbeginfullscreen', this.handleWebkitFullscreen);
      this.video?.removeEventListener('webkitendfullscreen', this.handleWebkitFullscreen);
      this.textTrack = undefined;
      this.captionRegion?.removeAttribute('hidden');
      if (this.cueElement) this.cueElement.textContent = '';

      if (!this.video) return;
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
      this.video.remove();
      this.video = undefined;
    }

    teardown() {
      this.startLink?.removeEventListener('click', this.handleStart);
      this.startLink?.removeEventListener('keydown', this.handleKeydown);
      this.captionToggle?.removeEventListener('click', this.handleCaptionToggle);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      this.removeVideo();
      this.initialized = false;
    }
  }

  customElements.define(PLAYER_TAG, LandingBrewGuidePlayer);
}

const TRANSCRIPT_TAG = 'landing-brew-guide-transcript';

if (!customElements.get(TRANSCRIPT_TAG)) {
  class LandingBrewGuideTranscript extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.details = this.querySelector('[data-brew-guide-transcript-details]');
      this.trigger = this.querySelector('[data-brew-guide-transcript-open]');
      this.content = this.querySelector('[data-brew-guide-transcript-content]');
      this.dialog = this.querySelector('[data-brew-guide-transcript-dialog]');
      this.body = this.querySelector('[data-brew-guide-transcript-body]');
      this.closeButton = this.querySelector('[data-brew-guide-transcript-close]');
      this.backdrop = this.querySelector('[data-brew-guide-transcript-dismiss]');

      if (!this.details || !this.trigger || !this.content || !this.dialog || !this.body ||
          !this.closeButton || !this.backdrop || typeof this.dialog.showModal !== 'function') return;

      this.initialized = true;
      this.openDialog = this.openDialog.bind(this);
      this.closeDialog = this.closeDialog.bind(this);
      this.handleCancel = this.handleCancel.bind(this);
      this.handleClosed = this.handleClosed.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.body.append(this.content);
      this.details.open = false;
      this.dataset.enhanced = 'true';
      this.trigger.setAttribute('aria-controls', this.dialog.id);
      this.trigger.setAttribute('aria-haspopup', 'dialog');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.addEventListener('click', this.openDialog);
      this.closeButton.addEventListener('click', this.closeDialog);
      this.backdrop.addEventListener('click', this.closeDialog);
      this.dialog.addEventListener('click', this.handleDialogClick);
      this.dialog.addEventListener('cancel', this.handleCancel);
      this.dialog.addEventListener('close', this.handleClosed);
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      window.clearTimeout(this.closeTimer);
      this.resolveClose?.();
      this.closePromise = null;
      this.trigger.removeEventListener('click', this.openDialog);
      this.closeButton.removeEventListener('click', this.closeDialog);
      this.backdrop.removeEventListener('click', this.closeDialog);
      this.dialog.removeEventListener('click', this.handleDialogClick);
      this.dialog.removeEventListener('cancel', this.handleCancel);
      this.dialog.removeEventListener('close', this.handleClosed);
      if (this.dialog.open) this.dialog.close();
      this.details.append(this.content);
      this.trigger.removeAttribute('aria-controls');
      this.trigger.removeAttribute('aria-haspopup');
      this.trigger.setAttribute('aria-expanded', 'false');
      delete this.dataset.enhanced;
      this.initialized = false;
    }

    openDialog(event) {
      event.preventDefault();
      if (this.dialog.open) return;
      delete this.dialog.dataset.brewDialogState;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.dialog.showModal();
      this.closeButton.focus({ preventScroll: true });
    }

    closeDialog() {
      if (!this.dialog.open) return Promise.resolve();
      if (this.closePromise) return this.closePromise;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.dialog.close();
        return Promise.resolve();
      }
      this.dialog.dataset.brewDialogState = 'closing';
      this.closePromise = new Promise((resolve) => {
        this.resolveClose = resolve;
        this.closeTimer = window.setTimeout(() => {
          if (this.dialog.open) this.dialog.close();
          resolve();
          this.closePromise = null;
          this.resolveClose = null;
        }, 200);
      });
      return this.closePromise;
    }

    handleCancel(event) {
      event.preventDefault();
      this.closeDialog();
    }

    handleDialogClick(event) {
      if (event.target === this.dialog || event.target === this.backdrop) this.closeDialog();
    }

    handleClosed() {
      window.clearTimeout(this.closeTimer);
      this.resolveClose?.();
      this.closePromise = null;
      this.resolveClose = null;
      delete this.dialog.dataset.brewDialogState;
      this.trigger.setAttribute('aria-expanded', 'false');
      if (this.trigger.isConnected) this.trigger.focus({ preventScroll: true });
    }
  }

  customElements.define(TRANSCRIPT_TAG, LandingBrewGuideTranscript);
}
